import 'ol/ol.css';
import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import Overlay from 'ol/Overlay';
import { isEmpty } from 'ol/extent';
import LayerGroup from 'ol/layer/Group';
import { ScaleLine } from 'ol/control';
import Zoom from 'ol/control/Zoom';
import { Attribution, defaults as defaultControls } from 'ol/control.js';
import DragPan from 'ol/interaction/DragPan';


export default class MapModel {
    constructor() {
        this.id = 'map';
        this.map = null;
        this.mapLayer = null

        this.dark = false;
        this.projections = [];
        this.projection = null;
        this.mapUrl = null;

        this.productionOverlay = null;
        this.deviceOverlay = null;

        this.mapElements = [];
    }

    initialize(dark, mapUrl, projections, projection, showcontrols = false) {
        this.dark = dark;
        this.projections = projections;
        this.projection = projection;
        this.mapUrl = mapUrl;

        this.registerProjections();
        this.generateOverlays();
        this.generateMap(showcontrols);
        this.setProjection(projection);

        this.initProj4();
    }

    addMapElement(mapElement) {
        this.mapElements.push(mapElement);
    }

    registerProjections() {
        this.projections.forEach(function (projection) {
            proj4.defs(projection.code, projection.def);
        });
        register(proj4);
    }

    async initProj4() {
        try {
            const rdtrans2018 = await this.loadGridFile('/rdtrans2018.gsb');
            proj4.nadgrid('rdtrans2018.gsb', rdtrans2018);

            const BETA2007 = await this.loadGridFile('/BETA2007.gsb');
            proj4.nadgrid('BETA2007.gsb', BETA2007);

            const OSTN15_NTv2_OSGBtoETRS = await this.loadGridFile('/OSTN15_NTv2_OSGBtoETRS.gsb');
            proj4.nadgrid('OSTN15_NTv2_OSGBtoETRS.gsb', OSTN15_NTv2_OSGBtoETRS);

            proj4.nadgrid('de_adv_BETA2007.tif', BETA2007);

            const us_noaa_nehpgn = await this.loadGridFile('/nehpgn.gsb');
            proj4.nadgrid('us_noaa_nehpgn.tif', us_noaa_nehpgn);

            const us_noaa_iahpgn = await this.loadGridFile('/iahpgn.gsb');
            proj4.nadgrid('us_noaa_iahpgn.tif', us_noaa_iahpgn);

            const MV_S4283_NTv2 = await this.loadGridFile('/MV_S4283_NTv2.gsb');
            proj4.nadgrid('MV_S4283_NTv2.gsb', MV_S4283_NTv2);

        } catch (error) {
            console.error('Error initializing proj4:', error);
        }
        register(proj4); // idk if needed but just add it to be sure
    }

    async loadGridFile(url) {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to load ${url}`);
        }
        return response.arrayBuffer();
    }

    setProjections(projections) {
        this.projections = projections;
        this.registerProjections();
    }

    generateOverlays() {
        var productionPopup = document.getElementById('production-popup');
        var devicePopup = document.getElementById('device-popup');
        this.productionOverlay = new Overlay({
            element: productionPopup,
            autoPan: true,
            autoPanAnimation: {
                duration: 250
            }
        });

        this.deviceOverlay = new Overlay({
            element: devicePopup,
            autoPan: true,
            autoPanAnimation: {
                duration: 250
            }
        });
    }

    hideMapLayer() {
        this.mapLayer.setVisible(false);
    }

    showMapLayer() {
        this.mapLayer.setVisible(true);
    }

    generateMap(showcontrols = false) {
        // var controls = [];
        // if (showcontrols) {
        //     controls = [
        //         new ScaleLine({
        //             units: 'metric',
        //         }),
        //         new Zoom()
        //     ];
        // }
        this.map = new Map({
            target: 'map',
            overlays: [this.productionOverlay, this.deviceOverlay],
            view: new View({
                zoom: 0,
                center: [0, 0],
                constrainResolution: false,
            }),
            controls: []
        });
        if (this.mapUrl != null) {
            this.mapLayer = new TileLayer({
                source: new OSM({
                    preload: Infinity,
                    url: this.mapUrl
                })
            })
            this.map.addLayer(this.mapLayer);
        }
        this.map.updateSize();
    }

    // // not implemented
    // deleteMapElement(mapElement) {
    //     mapElement.removeLayers();
    // }

    findLayer(mapElement) {
        return mapElement.getLayers();
    }

    showLayer(mapElement) {
        mapElement.show();
    }

    hideLayer(mapElement) {
        mapElement.hide();
    }

    setProjection(projection) {
        const hasGridFile = (proj) => {
            const projDefinition = proj4.defs(proj);
            return projDefinition && projDefinition.nadgrids;
        };

        // if (projection === "EPSG:28992_2018") {
        //     projection = "EPSG:28992";
        // } else 
        //TODO fix rd new projection
        this.showMapLayer();
        if (hasGridFile(projection)) {
            // this.hideMapLayer();
            projection = "EPSG:3857";
        }

        this.projection = projection
        this.map.setView(new View({
            center: this.map.getView().getCenter(),
            zoom: this.map.getView().getZoom(),
            projection: projection
        }));
    }

    showPopup(coordinate, popupContent) {
        switch (popupContent) {
            case "profilerProduction":
                this.productionOverlay.setPosition(coordinate);
                break;

            default:
                this.deviceOverlay.setPosition(coordinate);
                break;
        }
    }

    fitExtend(extent, options = { padding: [20, 20, 20, 20], maxZoom: 19 }) {
        if (!isEmpty(extent)) {
            this.map.getView().fit(extent, options);
        }
    }

    zoomMap(zoomValue) {
        this.map.getView().setZoom(this.map.getView().getZoom() + zoomValue);
    }

    getLonLatFromViewPortPx(xy) {
        return this.map.getLonLatFromViewPortPx(xy);
    }

    rotate(angle) {
        this.map.getView().setRotation(angle);
    }


    setPanEnabled(enabled) {
        this.map.getInteractions().forEach(interaction => {
            if (interaction instanceof DragPan) {
                interaction.setActive(enabled);
            }
        });
    }
}